import { useAnimation } from 'framer-motion';
import type { AnimationControls } from 'framer-motion';
import type { HTMLAttributes } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { cn } from '@/Domain/utils/cn';

export type AnimatedIconHandle = {
  startAnimation: () => void;
  stopAnimation: () => void;
};

type AnimatedIconProps = HTMLAttributes<HTMLDivElement> & {
  size?: number;
};

type IconRenderProps = {
  controls: AnimationControls;
  size: number;
};

export const withAnimatedIcon = <P extends object>(
  IconComponent: React.ComponentType<IconRenderProps & P>,
  displayName: string,
) => {
  const WrappedIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps & P>(
    ({ onMouseEnter, onMouseLeave, className, size = 28, ...props }, ref) => {
      const controls = useAnimation();
      const isControlledRef = useRef(false);

      useImperativeHandle(ref, () => {
        isControlledRef.current = true;

        return {
          startAnimation: () => controls.start('animate'),
          stopAnimation: () => controls.start('normal'),
        };
      });

      const handleMouseEnter = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
          if (!isControlledRef.current) {
            controls.start('animate');
          } else {
            onMouseEnter?.(e);
          }
        },
        [controls, onMouseEnter],
      );

      const handleMouseLeave = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
          if (!isControlledRef.current) {
            controls.start('normal');
          } else {
            onMouseLeave?.(e);
          }
        },
        [controls, onMouseLeave],
      );

      return (
        <div
          className={cn(className)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        >
          <IconComponent controls={controls} size={size} {...(props as P)} />
        </div>
      );
    },
  );

  WrappedIcon.displayName = displayName;

  return WrappedIcon;
};
